import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Inject, Input, Output, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipEvent, MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { Observable, combineLatest, debounceTime, map } from 'rxjs';
import { startWith, switchMap } from 'rxjs/operators';
import { CrmInjectionToken, CrmDependencies } from '../../../../tokens-and-interfaces';
import { TranslationModule } from '../../../../i18n/translation-module';
import { CrmObjectService, StandardIds } from '../../../../shared-services';

@Component({
  selector: 'crm-activity-logger-tags',
  templateUrl: './tags.component.html',
  imports: [
    CommonModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatChipsModule,
    MatIconModule,
    TranslationModule,
    MatAutocompleteModule,
    MatTooltipModule,
  ],
  standalone: true,
})
export class CrmActivityLoggerTagsComponent {
  @Input({ transform: coerceBooleanProperty }) mobile: boolean | null = null;

  @Input({ required: true }) set tags(value: string[]) {
    this.activityTags.set(value);
  }
  @Output() tagsChanged = new EventEmitter<string[]>();

  protected showErrors = false;

  noteTagsCtrl = new FormControl<string[]>([]);
  formGroup = new FormGroup({
    loggedNoteTags: this.noteTagsCtrl,
  });

  filteredTags$: Observable<string[]>;
  separatorKeyCodes: number[] = [ENTER, COMMA];
  tagsCtrl = new FormControl<string>('');
  protected readonly activityTags = signal<string[]>([]);

  constructor(
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    private readonly crmObjectService: CrmObjectService,
  ) {
    const tagTextSearch$ = this.tagsCtrl.valueChanges.pipe(startWith(''));
    this.filteredTags$ = combineLatest([tagTextSearch$, this.config.namespace$]).pipe(
      debounceTime(300),
      switchMap(([searchTerm, namespace]) => {
        return this.crmObjectService.listFieldOptions('Activity', {
          namespace: namespace,
          fieldId: StandardIds.ActivityTags,
          pageSize: 50,
          searchTerm: searchTerm ?? '',
        });
      }),
      map((resp) => resp.options ?? []),
    );
  }

  addNewTag(event: MatChipInputEvent): void {
    this.addTag(event.value);
    this.tagsCtrl.setValue(null);
  }

  selectedTag(tag: MatAutocompleteSelectedEvent): void {
    if (!tag?.option?.value) {
      return;
    }
    this.addTag(tag.option.value);
    this.tagsCtrl.setValue(null);
  }

  private addTag(tag: string): void {
    const tags = this.activityTags();
    if (!tags.includes(tag)) {
      tags.push(tag);
      this.activityTags.set(tags);
      this.noteTagsCtrl.setValue(tags);
      this.noteTagsCtrl.markAsDirty();
      this.tagsChanged.emit(tags);
    }
  }

  removeTag(event: MatChipEvent): void {
    const tag = event.chip.value;
    const tags = this.activityTags().filter((t) => t !== tag);
    this.activityTags.set(tags);
    this.noteTagsCtrl.setValue(tags);
    this.noteTagsCtrl.markAsDirty();
    this.tagsChanged.emit(tags);
  }
}
